import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useDomains, useUpdateDomain } from '../hooks/useDomains';
import type { Domain } from '../types/domain';

export default function DomainsPage() {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');

  const { data, isLoading } = useDomains();
  const updateMutation = useUpdateDomain();

  const handleEditStart = (item: Domain) => {
    setEditingId(item.id);
    setEditTitle(item.title);
  };

  const handleEditSave = async () => {
    if (editingId === null || !editTitle.trim()) return;
    try {
      await updateMutation.mutateAsync({ id: editingId, data: { title: editTitle.trim() } });
      setEditingId(null);
      toast.success('Domain updated');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update domain');
    }
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditTitle('');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h1 className="text-pine-display font-display text-pine-ink">Communication Pyramid</h1>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg border border-pine-line overflow-hidden"
      >
        <table className="w-full">
          <thead>
            <tr className="border-b border-pine-line">
              <th className="px-4 py-3 text-left text-xs font-medium text-pine-ink-soft uppercase tracking-wider w-20">Level</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-pine-ink-soft uppercase tracking-wider">Title</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-pine-ink-soft uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-pine-line">
            {isLoading && (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-pine-ink-soft">Loading...</td>
              </tr>
            )}
            {!isLoading && (!data || data.length === 0) && (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-pine-ink-soft">No domains found</td>
              </tr>
            )}
            {data?.map((item) => (
              <tr key={item.id} className="hover:bg-pine-hover-item">
                <td className="px-4 py-3 text-pine-ink-soft text-sm">{item.level}</td>
                <td className="px-4 py-3 text-pine-ink">
                  {editingId === item.id ? (
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleEditSave();
                        if (e.key === 'Escape') handleEditCancel();
                      }}
                      maxLength={100}
                      className="w-full px-2 py-1 rounded border border-pine-line bg-white text-pine-ink focus:border-pine-pine focus:outline-none transition-colors"
                      autoFocus
                    />
                  ) : (
                    item.title
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {editingId === item.id ? (
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={handleEditSave}
                        disabled={updateMutation.isPending}
                        className="px-3 py-1 text-xs rounded bg-pine-pine text-white hover:bg-pine-pine-deep disabled:opacity-50 transition-colors"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleEditCancel}
                        className="px-3 py-1 text-xs rounded border border-pine-line text-pine-ink-soft hover:bg-pine-hover-button transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleEditStart(item)}
                      className="px-3 py-1 text-xs rounded border border-pine-line text-pine-ink-soft hover:bg-pine-hover-button transition-colors"
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </motion.div>
    </motion.div>
  );
}
